import React, {Fragment, isValidElement, useEffect, useLayoutEffect, useRef, useState} from "react";
import {tailCheck, useObserver, useRerender, useStateEx} from "./CommonReact";
import {Button, Checkbox, TextInput} from "./Inputs";
import {Divider, ListItemValue, Title} from "../app/CommonUIComponenets";
import {Badge, TextEllip, TitleWithBadge} from "./UIBlocks";
import {commafyPlural, objToArrK} from "./CommonJS";
import {AnimatePickerRotation, icn} from "./UIIcons";
import {ToggleDiv} from "./UIAnimateToggleDiv";
import {genTooltip} from "./Tooltip";
import {notifTypes} from "../routes";
import {UIComboBox} from "./UIComboBox";
import {genOnKeys, MkSvg} from "./CommonHtml";
import {severitiesDefsArr} from "../app/CommonUIData";
import {useLocation} from "react-router-dom";


export function Switch({
	value, field, opts = undefined, onChange, className = "", tip = undefined, disabled = false
}) {
	const curIdx = opts?.findIndex(o => value === o.k) ?? value;

	return <div
		data-test={`${field}-switch`} {...genTooltip(tip, "middle")}
		className={`flex relative transition-all duration-75 ease-in-out bg-bg4 cursor-pointer p-1 h-6 w-12 rounded-xl 
             shadow-[inset_2px_2px_8px_rgba(0,_0,_0,_0.4)] ${className} ${disabled && "!cursor-not-allowed opacity-40"}`}
		onClick={() => !disabled && onChange(field, !value)} {...(typeof disabled === "string" && genTooltip(disabled, "middle"))}>
		<div className={`absolute min-w-0 ${curIdx ? "left-1.5 bg-prm-light" : "left-[55%] bg-prm-contrast"}
             duration-100 ease-in-out rounded-full transition-all h-4 aspect-square min-w-0 shrink-0 
             shadow-[-2px_2px_8px_rgba(0,_0,_0,_0.4)]`}/>
		{opts && <div className={`absolute ${!curIdx ? "left-1" : "left-[55%]"} transition-all text-title fill-title`}>
			{curIdx ? opts[0].i : opts[1].i}</div>}
	</div>;
}

export function UIRadioGroup({
	value, field, opts, onChange, className, classNameBtn, classNameN, classNameMark, classNameText, disabled,
	toggle, ...props
}) {
	const rBar = useRef();
	const rerender = useRerender();
	const curIdx = opts?.findIndex(o => value === o.k);
	const curElem = Array.from(rBar.current?.children || [])
		.filter(i => !i.classList.contains("absolute"))[curIdx];
	useObserver(ResizeObserver, rBar, "width", () => rerender(), {debounceMs: 100});   // allow animated box to redraw after initial layout or scrollbar appearance

    // Re-measure the marker whenever the options change (added/removed tabs, label or badge width) — container width alone may not change.
	const optsSig = opts?.map(o => `${o.k}\0${typeof o.h === "string"? o.h : ""}\0${o.n ?? ""}`).join("\x01");
	useEffect(() => {
		rerender();
	}, [optsSig]);

	return (
		<div
			className={tailCheck(className, "select-none flex relative", "w-max",
																								disabled && "opacity-60", !toggle? "cursor-default" : "cursor-pointer")}
			onClick={disabled || !toggle? undefined : () => onChange(field, opts[(curIdx) % opts.length].k)}
			{...props} ref={rBar} draggable="false">
			{curElem &&
                <div
	className={tailCheck(classNameMark, "absolute transition-all duration-300 ease-out", "bg-gray-800/80")}
	style={{width: curElem?.offsetWidth + "px", height: (curElem?.offsetHeight) + "px", transform: `translate(${curElem?.offsetLeft}px,${curElem?.offsetTop}px)`}}/>}
			{opts?.map((opt) => (
				<div
					data-test={`ui-radio-group-${String(opt.k).toSnakeCase()}`}
					key={opt.k} {...genTooltip(opt.tip)}
					onClick={() => opt.k !== value && !disabled && !opt.disabled && onChange(field, opt.k)}
					className={tailCheck(classNameBtn,
																										"flex items-center gap-2 transition ease-out duration-300 z-[1] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75",
																										"justify-center",
																										opt.k === value? "X bg-transparent" :
																											!disabled && !opt.disabled? "cursor-pointer opacity-60 hover:opacity-100" :
																												"cursor-not-allowed opacity-40",
																										!classNameBtn?.match(/(^| )p\w?-/) && "px-2 py-1"
					)}>
					<TextEllip className={`${opt.k === value && "text-prm-contrast"} ${classNameText}`}>{opt.h}</TextEllip>
					{opt.n !== undefined && <Badge pill className={`${classNameN} shrink-0 bg-subtitle1/40 ${opt.k !== value? "text-title" : "text-prm-contrast"}`}>{opt.n}</Badge>}
				</div>
			))}
		</div>
	);
}

export const FilterRadioGroup = ({
	className, wrap = false, classNameBtn, disabled = false, ...rest
}) =>
	<UIRadioGroup
		className={tailCheck(className, "bg-bg2", "rounded-md", "overflow-hidden")} disabled={disabled}
		classNameMark="bg-prm-main" classNameBtn={tailCheck(classNameBtn, "text-2xs", "border-r-subtitle2 border-r last:border-r-transparent",
																																																						wrap ? "px-3 py-2 flex-1 text-center" : "px-4 py-3 whitespace-nowrap")}
		{...rest}/>;

export const FilterList = ({w = "", horizontal, ...rest}) =>
	<UIRadioGroup
		className={`bg-transparent gap-2 flex-wrap w-full ${!horizontal && "flex-col"}`}
		classNameBtn={`bg-bubble text-xs rounded-md px-4 py-2 justify-between min-w-0 ${horizontal ? "" : "w-full"} ${w}`}
		classNameMark="bg-prm-main rounded-md"
		{...rest}/>;


export function UIMultiSelect({
	selKeys, field, opts, onChange, className, classNameBtn, classNameMark, disabled, allIfNone, ...props
}) {
	selKeys = selKeys ?? [];
	const isAllState = allIfNone && !selKeys.length;
	const hasNoAllMark = opts.some(o => o.noAllMark);
	const impliedKeys = opts.filter(o => !o.noAllMark).map(o => o.k);       // The opts an empty selection implies.
	return (
		<div
			className={tailCheck(className, "flex flex-wrap", "w-fit", "relative select-none", disabled && "opacity-60")}
			draggable="false" {...props}>
			{opts.map((opt) => {
				const isSel = selKeys.includes(opt.k);
				const isAll = isAllState && !opt.noAllMark;
				const addBase = isAllState && opt.noAllMark ? impliedKeys : selKeys;    // Adding a noAllMark opt joins the implied opts instead of replacing them.
				const onClick = () => {
					let nextSelKeys = isSel ? selKeys.filter(k => k !== opt.k) : [...addBase, opt.k];
					const isImpliedSet = nextSelKeys.length === impliedKeys.length && impliedKeys.every(k => nextSelKeys.includes(k));
					if (hasNoAllMark && isImpliedSet)
						nextSelKeys = [];
					onChange(field, nextSelKeys);
				};
				return (
					<div
						key={opt.k}
						data-test={`${opt.k}-multiselect`}
						onClick={() => !disabled && onClick()}
						{...genTooltip(opt.tip)}
						className={tailCheck(classNameBtn,
																											"inline-flex items-center leading-none transition ease-out duration-300 z-[1]",
																											"focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75",
																											"px-2 py-1", "justify-center",
																											(!isSel && !isAll) && !disabled && "opacity-50 hover:opacity-75",
																											(isSel || isAll) && (classNameMark || "text-white bg-prm-main"),
																											!disabled? "cursor-pointer" : "cursor-not-allowed")}>
						{opt.h}
					</div>
				);
			})}
		</div>
	);
}

export function SeverityPicker({
	field, selKeys, onChange, severityVals, className="", classNameBtn="", defs = undefined
}) {
	const defsArr = defs ? objToArrK(defs) : severitiesDefsArr;
	const getDef = sevKey => typeof sevKey === "string" ? defsArr.find(def => def.k === sevKey) : defsArr[sevKey];  // Keys can be def keys (severityScore) or legacy array positions.
	return <MultiSelectListItems
		{...{field, selKeys, onChange}}
		opts={severityVals.filter(({k: sevKey}) => getDef(sevKey)).map(({k: sevKey, v: count}) => {
			const def = getDef(sevKey);
			return {k: sevKey, v: count, h: def.h, border: def.border, noAllMark: def.noAllMark};
		})}
		className={`flex ${severityVals?.length === 4 && "w-"} ${className}`}
		classNameBtn={`min-w-14 m- ${severityVals?.length < 4 && "grow-"} ${classNameBtn}`}/>;
}

export function FilterListItems({
	field, value, onChange, opts, className = "", classNameBtn = "", classNameItem = "", classNameMark = ""
}) {
	opts = opts.map(({
		k, h, v, border, ...rest
	}) => ({k,
		...rest,
		h:
            <ListItemValue
	h={v !== undefined? h : undefined} val={v ?? h}
	className={tailCheck(classNameItem, "w-full", "pb-1 overflow-visible", "text-2xs", "border-b-[3px]", border)}/>}));

	return <UIMultiSelect
		{...{field, selKeys: value && [value], opts}} onChange={(f, v) => onChange(f, v.filter(k => k!==value)?.[0] ?? value)}
		className={tailCheck(className, "shrink-0 rounded-md bg-bg1/40", !className?.match(/(^| )flex/) && "grid grid-cols-2 w-")}
		classNameBtn={tailCheck(classNameBtn, "text-xs text-center", "rounded border-r-bg2 not-last:border-r py-1", "grow", "m-1")}
		classNameMark={tailCheck(classNameMark, "bg-bg1")}/>;
}

export function MultiSelectListItems({
	field, selKeys, onChange, opts, className, classNameBtn, classNameItem = ""
}) {
	opts = opts.map(({
		k, h, v, c, border, ...rest
	}) => ({k,
		...rest,
		h:
            <ListItemValue
	h={v !== undefined? h : undefined} val={v ?? h}
	style={c && {borderColor: c}}
	className={tailCheck(classNameItem, "w-full", "pb-1 overflow-visible", "text-2xs", "border-b-[3px]", border)}/>}));

	return <UIMultiSelect
		{...{field, selKeys, onChange, opts}} allIfNone
		className={tailCheck(className, "shrink-0 rounded-md bg-bg1/40", !className?.startsWith("flex") && "grid grid-cols-2 w-")}
		classNameBtn={tailCheck(classNameBtn, "text-xs text-center rounded border-r-bg2 not-last:border-r py-1", "grow", "m-1")}
		classNameMark="bg-bg1"/>;
}

// PillsFilter: select UI for multi (default) or single. Active pill = filled bg. opt.icon (with opt.iconClassName) renders on each pill when provided.
export function PillsFilter({
	field, value, onChange, opts, isMulti = true, title = "", noTitle = false, classNameBtn = "", classNameBtnSelected = "", crossedNonSelect = false, noWrap = false
}) {
	const selKeys = isMulti ? (value ?? []) : value !== undefined && value !== null ? [value] : [];
	const displayTitle = isMulti ? `${title || "View"} Filters` : title;
	return <div className={`flex items-center gap-1 ${noWrap ? "" : "flex-wrap"}`} data-test={`${title ? title.toLowerCase() : "view"}-filter`}>
		{!noTitle && displayTitle && <Title classNameColor="text-h2">{displayTitle}:</Title>}
		{opts.map(opt => {
			const active = selKeys.includes(opt.k);
			return <Button
				key={opt.k} data-test={`pill-${String(opt.k).toSnakeCase?.() ?? opt.k}`}
				onClick={() => onChange(field, isMulti ? selKeys.toggleItem(opt.k) : active ? undefined : opt.k)}
				{...(opt.tip && genTooltip(opt.tip))}
				className={tailCheck(classNameBtn, "gap-1", isMulti && active ? "pl-2 pr-3" : "px-3", "py-1", "transition-all", "rounded-full", "border border-prm-main", "flex items-center",
																									active
																										? tailCheck(classNameBtnSelected, "bg-prm-main", "text-prm-contrast", "border-bg1")
																										: `bg-bg1 opacity-80 hover:opacity-100 ${crossedNonSelect ? "line-through" : ""}`)}>
				{isMulti && active
					? <div><MkSvg className="h-3 w-3" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/></div>
					: opt.icon && <span className={opt.iconClassName}>{opt.icon}</span>}
				<Title classNameColor="max-w-96 text-xs">{opt.h}</Title>
			</Button>;
		})}
	</div>;
}

// Tri-state pills: each item can be neutral, included, or excluded.
// sel is {key: "include"|"exclude"|undefined}. Exclude badge appears on hover.
export function PillsIncludeExclude({
	field, sel = {}, onChange, opts, emptyMsg = "No items found", withExclude = true
}) {
	if (!opts?.length)
		return <Title className="text-xs title-desc">{emptyMsg}</Title>;
	const setMode = (name, mode) => {
		const next = {...sel};
		const newMode = sel[name] === mode ? undefined : mode;
		if (newMode) next[name] = newMode; else delete next[name];
		onChange(field, next);
	};
	return (
		<div className="flex gap-y-1.5 flex-wrap">
			{opts.map(name => {
				const state = sel[name];
				return (
					<div key={name} data-test={`pill-ie-${name}`} className="group/cat flex items-center relative">
						<Button
							onClick={() => setMode(name, "include")}
							className={`text-xs px-3 py-1 rounded-full border transition-colors ${
								state === "include" ? "bg-prm-main text-prm-contrast border-prm-main"
									: state === "exclude" ? "bg-threat/80 text-white border-threat line-through"
										: "bg-bg1 border-prm-main opacity-80 hover:opacity-100"}`}>
							{name}
						</Button>
						{withExclude && (
							<div className="absolute top-1.5 right-1.5">
								<Button
									onClick={e => { e.stopPropagation(); setMode(name, "exclude"); }}
									data-test="pill-ie-exclude"
									className={`icon-cross shrink-0 w-4 h-4 -ml-1.5 text-[0.65rem] rounded-full border border-threat flex items-center justify-center transition-opacity text-white ${
										state === "exclude" ? "opacity-100 bg-prm-main" : "bg-threat opacity-0 group-hover/cat:opacity-100"}`}/>
							</div>
						)
						}
					</div>
				);
			})}
		</div>
	);
}

export function UICheckBoxGroup({
	field, opts, onChange, className, checkboxClassName = "", value, row = false
}) {
	const changeChecked = (checkedBox) => {
		const newVal = value.includes(checkedBox) ? value.filter(k => k !== checkedBox) : [checkedBox, ...value];
		onChange(field, newVal);
	};

	return <div className={`flex ${className} ${row ? "gap-4" : "gap-1 flex-col"}`}>
		{opts.map(opt =>
			<Checkbox
				data-test={`${opt.h}-checkbox`}
				key={opt.k}
				className={`${row ? "gap-1" : "gap-3"} ${checkboxClassName}`}
				field={opt.k}
				isChecked={value.includes(opt.k)}
				label={
					<div className="flex items-center gap-1">
						{opt.c && <div className={`w-2 h-2 mr-1 ${opt.c}`} style={{background: opt.c}}/>}
						<Title>{opt.h}</Title>
					</div>}
				onChange={changeChecked}/>)}
	</div>;
}

/**
 * Displays an animated tabs row.
 * @param tabs array of tabs: [string | function | "sep" | {h?: string, d?(): renderer, unavail?: bool, className?: string}]
 * @param onClick function(index): called when user clicks on a tab.
 * @param iTab number: index of currently selected tab.
 * @param className string?: top level classes
 * @param classNameTab string?:  common classes for tab elements
 * @param classNameUnderline string?: classes for dynamic underline. default: "top-8"
 * @param renderer function(tab, i)?:
 */
export function UITabsBar({
	tabs, onClick, iTab, className, classNameTab, classNameUnderline = undefined, renderer = undefined
}) {
	const rBar = useRef();
	const rITab = useRef(iTab);
	rITab.current = iTab;                            // keep current for the observer cb, which is captured once.
	const rerender = useRerender();                 // still handed to tab renderers for their own redraws.
	const {state, setState} = useStateEx({width: 0, left: 0});

	const measure = () => {                          // read active-tab geometry into state; +1 skips the underline div.
		const el = rBar.current?.children[rITab.current + 1];
		if (el) setState({width: el.offsetWidth, left: el.offsetLeft});
	};

	useLayoutEffect(measure, [iTab, tabs]);          // measure before paint, so the underline is right on the first committed frame.
	useObserver(ResizeObserver, rBar, "width", measure, {debounceMs: 100});   // re-measure on resize / font-size jump.
	useEffect(() => void document.fonts?.ready.then(measure), []);            // re-measure after web-font load (FOUT).

	return (
		<div data-test="ui-tab-bar" className={`relative flex gap-2 whitespace-nowrap ${className}`} ref={rBar}>
			<div
				style={{width: state.width + "px", height: "3px", transform: `translateX(${state.left}px)`}}
				className={`transition-all duration-300 ease-out absolute bg-prm-main ${classNameUnderline || "top-8"}`}/>
			{tabs.map((tab, i) => tab === "sep"?
				<div key={i} className="mx-4 h-3 mt-3 bg-gray-400 w-[2px] focus:outline-none"/>
				: <Button
					data-test={`${i}-tab`}
					key={i} {...genTooltip(tab.tip)}
					onClick={iTab === i || tab.unavail? undefined : (() => onClick(i))}
					className={`px-3 text-center z-10 border-transparent ${
						!classNameTab?.match(/(^| )text-/) && "text-title"} ${
						iTab !== i? "[transition-property:all] opacity-50 hover:opacity-70" : "opacity-100"} ${
						tab.unavail && "cursor-not-allowed !opacity-30"} ${
						tab.className} ${
						classNameTab}`}>
					{renderer ? renderer(tab, i) :
						typeof tab === "string" || isValidElement(tab)? tab :
							typeof tab === "function"? tab(i, rerender) :
								(tab.d?.(tab, rerender) ?? tab.h)}
				</Button>
			)}
		</div>
	);
}

export const TabsWithNum = (tabs, tipFormat) => tabs.map(tab => ({...tab, d: ({h, v}) => <TitleWithBadge val={v} {...{h, tipFormat}}/>}));

export function MultiValuesBase({
	field, value, onChange, classNameItemText, dispAdd
}) {
	return <>
		{value?.map((v, i) =>
			<div key={i} className="flex rounded-full border border-tx-steel py-0.5 px-2 items-center gap-2 bg-bubble">
				<TextEllip className={classNameItemText}>{v}</TextEllip>
				<Button h={icn.remove35} onClick={() => onChange(field, value.toggleItem(v))} className="mr-"/>
			</div>)}
		{dispAdd?.({onAdd: v => onChange(field, [...value ?? [], v])})}
	</>;
}

/**
 * MultiValuesInput Component
 *
 * This component allows users to manage a list of values by adding new values
 * and removing existing ones. It provides an input field for entering new values
 * and displays the current list of values with an option to remove them.
 *
 * @param {string} field - The name of the field being managed.
 * @param {Array} value - The current list of values.
 * @param {Function} onChange - Callback function to handle changes to the list of values.
 * @param classNameItemText
 * @param {number} maxVals - The maximum number of values allowed in the list.
 * @param {Function} checkSave - Optional validation function for new values. Should return a boolean indicating if the value is valid.
 * @param onChangeEdit
 */
export function MultiValuesInput({
	field, value, onChange, classNameItemText = "", maxVals = undefined, checkSave = undefined, onChangeEdit = undefined
}) {
	const [newVal, setNewVal] = useState("");
	const rInput = useRef();

	function validateSave(v, checkEmpty = true) {
		if (!v) return checkEmpty && "Enter a value to add to the list.";
		if (value.includes(v)) return "Value already exists.";
		return checkSave?.(v);
	}

	function handleAdd(onAdd) {
		onAdd(newVal);
		setNewVal("");
		onChangeEdit?.("");
		rInput.current.focus();
	}

	return <MultiValuesBase
		{...{field, value, onChange, classNameItemText}}
		dispAdd={({onAdd}) => (!maxVals || value.length < maxVals) &&
            <div className="flex items-center gap-3">
            	<TextInput
	ref={rInput} value={newVal} onChange={(f, v) => (setNewVal(v), onChangeEdit?.(v))}
	{...genOnKeys({Enter: () => !validateSave(newVal) && handleAdd(onAdd)})}
	validate={v => validateSave(v, false)} className="w-36 rounded-full py-0.5 px-2 " dataTest="newVal"/>
            	<Button h="Add" disabled={validateSave(newVal)} data-test="add-button" className="text-prm-light text-xs" onClick={() => handleAdd(onAdd)}/>
            </div>
		}/>;
}

export function MultiValuesOpts({
	field = "", value = undefined, onChange = undefined, classNameItemText = undefined,
	opts = undefined, comboProps = undefined, maxVals = undefined
}) {
	const noOpts = opts?.length === 0;
	opts = opts?.filter(v => !value?.includes(v));
	return <MultiValuesBase
		{...{field, value, onChange, classNameItemText}}
		dispAdd={({onAdd}) =>
			(opts?.length || (!opts && comboProps?.onPopPanel) || noOpts) &&
            (!maxVals || (value?.length ?? 0) < maxVals) &&
            <UIComboBox
	placeholder="Add" selectMode noCount selKeepOpen opts={opts} onSelect={(f, v) => onAdd(v)}
	{...comboProps} className="w-[4.7rem] rounded-full px-2 py-0.5 text-xs" stylePopPanel={{width: "20rem"}}/>
		}/>;
}

export function MenuTree({
	menuArr, className, notifsPaths, classNameText, field, value, onChange, depth = 0, classNameSub,
	classNameHead, classNameSelSub, onSelect, canSelectHead = false, openAbove = false, disabled
}) {
	const selPath = findSubPath({menu: menuArr, value});
	const [subOpen, setSubOpen] = useState(null);
	const selPathSynced = useRef(null);

	useEffect(() => {
		if (!selPath || selPath === selPathSynced.current)
			return;

		selPathSynced.current = selPath;
		setSubOpen(selPath);
	}, [menuArr, value]);

	const menuItem = (itm, path, depth = 0) => {
		const {
			h, icon, subMenu, staticMenu, className: classNameItm, k, params, tip, openAbove, n
		} = itm;

		const isSelected = path === selPath || value === k;
		const openSubMenu = () => setSubOpen(prev => prev === path ? path.substring(0, path.lastIndexOf(":")) : path);
		const currNotifs = notifsPaths?.filter(({path: p}) =>
				p.startsWith(path.slice(0, -1))), notifsCount = currNotifs?.reduce((acc, val) => acc + val.count, 0);

		const onClick = () => {
			subMenu && openSubMenu();
			if (k && (!subMenu || canSelectHead)) {
				onChange?.(field, k);
				onSelect?.(k, params, itm);
			}
		};

		const testId = k? k.toLowerCase().toKebabCase(): h?.toLowerCase().toKebabCase();

		return <Fragment key={path}>
			<Button
				data-test={`${field}-menu-item`}
				className={`${tailCheck(classNameItm || (!depth ? classNameHead : classNameSub),
																												"gap-2", "rounded-lg", "h-10", "py-2", depth ? "px-2" : "mt-6")} ${
					isSelected && ((depth && classNameSelSub) ? classNameSelSub : "active:bg-blue-500 bg-prm-main text-prm-contrast")}`}
				style={{marginLeft: (!depth ? 0 : depth - 1) + "rem"}} disabled={!k && !subMenu || disabled || itm.disabled}
				onClick={staticMenu ? undefined : onClick}
				{...genTooltip(tip)}>
				{!depth ? null : typeof icon === "string" ? <i className={`icon-${icon} w-4 text-base`}/> :
					<div className={isSelected ? "fill-prm-contrast" : "fill-subtitle1"}>{icon}</div>}
				<Title
					className={tailCheck(classNameText, "min-sm:grow", "grow", "font-light", depth ? "text-2sm" : "text-lg")}
					classNameColor={"text-inherit"} data-test={`${testId}-menu-item-title`}>{h}</Title>
				{!!currNotifs?.length && (!subMenu || subOpen !== path && !subOpen?.startsWith(path)) &&
                    <div
	{...genTooltip(Object.entries(currNotifs.groupBy(n => n.action)).map(([action, ntfs]) =>
                    		`${commafyPlural(ntfs?.reduce((acc, val) => acc + val.count, 0), "record")} of ${
                    			ntfs.map(v => v.desc).join(", ")} are pending ${action}.`).join("\n"))}
	className="shrink-0 px-1 text-2xs text-center animate-appear bg-sec/60 rounded-full text-prm-contrast">{notifsCount}</div>}
				{n && <Badge pill className={`shrink-0 opacity-60 hover:opacity-100 bg-subtitle1/40 text-prm-contrast text-xs ${isSelected ? "text-title" : "text-prm-contrast"}`}>{n}</Badge>}
				{subMenu && !staticMenu && <AnimatePickerRotation className={subOpen?.startsWith(path) ? "rotate-180" : "font-bolder"}/>}
			</Button>
			{subMenu &&
                <ToggleDiv show={staticMenu || subOpen?.startsWith(path)} className={`max-sm:overflow-x-hidden ${openAbove && "flex flex-col-reverse"}`} dataTest={testId}>
                	{subMenu.map((item, idx) => menuItem(item, `${path}${idx}:`, depth + 1))}
                </ToggleDiv>}
		</Fragment>;
	};

	return (
		<div className={`flex ${openAbove ? "flex-col-reverse" : "flex-col"} gap-0 ${className}`}>
			{menuArr.map((item, idx) => menuItem(item, idx + ":", depth))}
		</div>
	);
}

export function findNotifPath(menu, type, count, path) {
	return menu.findPred(({subMenu, notifs, pt}, i) =>
		(pt = (path ? path : "") + i + ":") &&
        Object.keys(notifs || []).includes(type) ?
			({path: pt, desc: notifs[type], count, action: notifTypes[type].action}) :
			subMenu && findNotifPath(subMenu, type, count, pt)
	);
}

function findSubPath({menu, path, value}) {
	return menu.findPred(({k, subMenu, pt}, i) =>
		(pt = `${path || ""}${i}:`) && (value === k || value?.startsWith(k))? pt : subMenu && findSubPath({menu: subMenu, value, path: pt}));
}

export function findPathEntries({menu, value}) {
	const path = findSubPath({menu, value});
	return path?.split(":").slice(0, -1).reduce((a, b) => ({
		p: [...a.p, a.menu[b*1]],
		menu: a.menu[b*1].subMenu
	}), {p: [], menu}).p;
}


export function HorizontalScrollArrows({
	children, className = "", cardMinWidth = "", ...props
}) {
	const scrollRef = useRef(null);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);

	useEffect(() => {
		const handleScroll = () => {
			const el = scrollRef.current;
			if (!el) return;
			setCanScrollLeft(el.scrollLeft > 0);
			setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
		};
		handleScroll();
		const el = scrollRef.current;
		if (el) {
			el.addEventListener("scroll", handleScroll);
			window.addEventListener("resize", handleScroll);
		}
		return () => {
			if (el) el.removeEventListener("scroll", handleScroll);
			window.removeEventListener("resize", handleScroll);
		};
	}, []);

	const scrollBy = (dir) => {
		const el = scrollRef.current;
		if (!el) return;
		const scrollAmount = el.clientWidth * 0.7;
		el.scrollBy({left: dir === "left" ? -scrollAmount : scrollAmount, behavior: "smooth"});
	};

	return (
		<div className="relative w-full">
			{canScrollLeft && (
				<div
					className="w-fit h-full text-xl flex items-center cursor-pointer hover:bg-bg2/70 !absolute top-1/2 -translate-y-1/2 left-0 z-10 bg-bg2/80 hover:bg-bg2 px-2 py-1 shadow"
					style={{pointerEvents: "auto"}} onClick={() => scrollBy("left")} aria-label="Scroll left"><MkSvg className="h-5 w-5" strokeWidth="3" d="M15 19l-7-7 7-7"/></div>
			)}
			{canScrollRight && (
				<div
					className="mr-0 w-fit h-full text-xl flex items-center  cursor-pointer hover:bg-bg2/70 !absolute top-1/2 -translate-y-1/2 right-0 z-10 bg-bg2/80 hover:bg-bg2 px-2 py-1 shadow"
					style={{pointerEvents: "auto"}} onClick={() => scrollBy("right")} aria-label="Scroll right">
					<MkSvg className="h-5 w-5" strokeWidth="3" d="M9 5l7 7-7 7"/>
				</div>
			)}
			<div className={`w-full ${className}`} {...props}>
				<div
					ref={scrollRef}
					className="flex gap-3 w-full pb-3 overflow-x-auto scrollbar-thin scrollbar-thumb-badge/75 scrollbar-track-bg2"
					style={{scrollBehavior: "smooth"}}>
					{React.Children.map(children, child =>
						React.isValidElement(child)
							? React.cloneElement(child, {
								style: {minWidth: cardMinWidth, ...(child.props.style || {})},
							})
							: child
					)}
				</div>
			</div>
		</div>);
}


export  const LocationTabsWrap = ({tabs, children, onClick}) => {
	const location = useLocation();
	const tabIdx = tabs.findIndex(t => t.to.indexOf(location.pathname) === 0);

	return (
		<div className="flex flex-col gap-3 h-full">
			<UITabsBar
				tabs={tabs} iTab={tabIdx}
				onClick={onClick}
				className="" classNameTab="text-lg h-8 text-title" classNameUnderline="top-11"/>
			<Divider/>
			{children}
		</div>
	);
};
